'use strict';

/**
 * T1123 — the Module Lock's CLIENT half.
 *
 * `auth/module-lock-edit-gate.js` has been the TEETH of Module Lock since T315
 * P8, and S321 extended it to reads. Both halves are correct and tested. What
 * was missing was the other end of the wire: measured at trunk `c632affa3`, an
 * AST census of every client `/auth/vault/blob` call site found 17 sites across
 * 8 files and NOT ONE of them sends an `unlockProof` or mints an S321
 * read-grant. `FEATURE_MODULE_LOCK` was a switch wired to nothing on the
 * browser side — flipping it would have bricked every locked module rather than
 * protected any.
 *
 * ── CHALLENGE-RESPONSE, NOT PRE-EMPTIVE ─────────────────────────────────────
 * Nothing here activates until the server answers `403 {reason:'module-lock'}`.
 * That shape is chosen for the SURVIVAL pole, and it is structural rather than
 * incidental:
 *
 *   - with the flag OFF the server never emits that refusal, so this wrapper
 *     issues EXACTLY the requests the product issues today, with the caller's
 *     own options object untouched. Flag-OFF is byte-identical BY CONSTRUCTION,
 *     not by remembering to check a flag the browser cannot see anyway.
 *   - a pre-emptive design would have to fetch the key envelope and mint a
 *     grant before every read on every account, including the overwhelming
 *     majority that have no lock at all. That is two extra round trips per read
 *     bought for nothing.
 *
 * It is the same idiom `shared/crypto/jit-fetch.js` already uses one layer down
 * on this exact transport, deliberately repeated rather than merged: JIT asks
 * "may this session reach this content at all", the lock asks "has the owner
 * opened THIS module". Folding them would make one flag's refusal able to
 * trigger the other's dance.
 *
 * ── WHAT THE SERVER WILL ACCEPT, MIRRORED EXACTLY ───────────────────────────
 *   WRITE (PUT / DELETE)  `req.body.unlockProof` — BODY ONLY. `vault-routes.js`
 *                         declined a `?proof=` fallback here on purpose; a live
 *                         proof-of-knowledge verifier in a query string lands in
 *                         every nginx access log and browser history entry
 *                         (GOTCHA-510 treats `removeProof` as a password hash).
 *   READ  (GET)           `POST /blob/:id/read-grant {unlockProof}` then
 *                         `GET /blob/:id?grant=<id>`. A GET carries no body, so
 *                         the proof rides the POST and the GET carries only an
 *                         opaque, one-shot, session-bound id.
 *
 * The proof itself is `sha256(moduleMaster || salt)` — the `removeProof`
 * primitive. The server holds only the expected value; it never sees the
 * passphrase or the master, so custodian-of-ciphertext is unchanged.
 *
 * ── THE CANONICAL ID IS NOT COSMETIC ────────────────────────────────────────
 * The gate resolves policy and expected proof through `canonicalForBlob`
 * (LD-14), so a lock on `notes` covers the split `notes-host` blob. A client
 * that proved against the BLOB id would compute a proof for a module the server
 * never asked about and be refused forever, on exactly the surfaces the split
 * exists for. This mirrors the server's resolution rather than re-deciding it.
 *
 * ── REFUSAL HONESTY ─────────────────────────────────────────────────────────
 * Every path that cannot produce a proof returns the SERVER'S OWN response
 * unchanged. This wrapper never invents a success, never swallows a refusal
 * into a 200, and never retries more than once (so a server that keeps refusing
 * cannot be turned into a request loop by a client bug).
 *
 * Browser-safe: no Node-only API beyond what the crypto shim already provides
 * to `remove-proof.js`, which this file's only two dependencies are.
 */

const { canonicalForBlob } = require('./module-id-map');
const { computeRemoveProof } = require('./remove-proof');

const BLOB_PREFIX = '/auth/vault/blob/';
const KEY_PREFIX = '/auth/vault/me/key/';

/**
 * The ONLY refusal reason a proof can clear.
 *
 * A FROZEN single value, never "any 403". The gate deliberately answers a
 * DIFFERENT reason (`module-lock-policy-unreadable`) when the stored policy
 * cannot be read, precisely because no proof can repair a corrupt file —
 * retrying that one walks the owner into a loop that can never clear, which is
 * the PSA-16 "restarts into the same refusal forever" class. Anything that is
 * not this exact token is passed through untouched.
 */
const RETRYABLE_REASON = 'module-lock';

/**
 * `/auth/vault/blob/<id>` and nothing else — not the list route, not sub-paths.
 *
 * ⚠ Written as a LITERAL regex rather than `s.indexOf(BLOB_PREFIX)` on purpose.
 * The FLIP-5 gated-caller census (`__tests__/helpers/gated-caller-census.js`)
 * starts from the CALL and records any callee whose resolved argument matches a
 * gated prefix — so passing the prefix constant into `indexOf` registers a
 * STRING COMPARISON as an unwrapped network call site. That is a false positive
 * in the instrument, and the cheap ways to silence it are worse than this: I am
 * not editing a shared instrument that other guards depend on, and I am not
 * adding a named exemption that would also cover the next file to trip it.
 */
const BLOB_URL_RE = /^\/auth\/vault\/blob\/([^/?#]+)(?:[?#]|$)/;

function blobIdFromUrl(url) {
  // A sub-path (`.../read-grant`) is a DIFFERENT resource and is issued by this
  // module itself; the `[^/?#]+` class refuses it, which keeps the wrapper from
  // ever re-entering.
  const m = BLOB_URL_RE.exec(String(url));
  if (!m) return null;
  return decodeURIComponent(m[1]);
}

function withQuery(url, name, value) {
  const s = String(url);
  const sep = s.indexOf('?') === -1 ? '?' : '&';
  return s + sep + name + '=' + encodeURIComponent(value);
}

async function readJsonSafely(res) {
  // A real browser Response body can be consumed ONCE. Read a clone where the
  // transport provides one so a pass-through response is still readable by the
  // caller; the test/http transports hand back re-readable objects.
  const src = res && typeof res.clone === 'function' ? res.clone() : res;
  try { return await src.json(); } catch (_e) { return null; }
}

/**
 * @param {object} deps
 * @param {function} [deps.baseFetch] — the transport to sit on top of. When it
 *   is omitted the DEFAULT is the shared JIT-grant wrapper
 *   `window.OysterVault.jitFetch`, resolved at CALL time — and when neither is
 *   available this THROWS. There is deliberately NO fallback to the bare global
 *   `fetch`: `/auth/vault/blob` is a JIT-gated content prefix, so a bare-fetch
 *   default would silently strip the grant and take a 400 JIT_HEADER_MISSING
 *   the moment `JIT_GRANT_ENFORCEMENT=1`. Defaulting to the SAFE wrapper is
 *   strictly better than requiring an argument a caller can get wrong: the
 *   hazard was never "someone forgot to pass one", it was "someone passed the
 *   unwrapped one".
 * @param {function} deps.resolveMaster — REQUIRED. `(canonicalModuleId) =>
 *   Uint8Array|Buffer|null`. Called at REFUSAL time, never cached: the master
 *   can arrive (or be auto-relocked away) at any point in a page's life.
 * @param {function} [deps.requireUnlock] — OPTIONAL. `async (canonicalModuleId)
 *   => void`; the step-up prompt. Omission is the STRICTER pole on purpose — a
 *   caller with no unlock UI simply relays the server's refusal, so a surface
 *   that never learns about this parameter can only be under-served, never
 *   under-gated.
 * @returns {function} `(url, opts) => Promise<Response>`
 */
function createBlobLockFetch({ baseFetch, resolveMaster, requireUnlock }) {
  if (baseFetch !== undefined && typeof baseFetch !== 'function') {
    throw new TypeError('createBlobLockFetch: baseFetch, when supplied, must be a function');
  }
  if (typeof resolveMaster !== 'function') {
    throw new TypeError('createBlobLockFetch: resolveMaster is REQUIRED — without it the wrapper '
      + 'can never compute a proof and would quietly degrade to a plain transport');
  }

  /**
   * EVERY request this module issues goes through here — the blob GET/PUT/DELETE
   * retries and the key-envelope reads alike.
   *
   * Resolved PER CALL, never cached: `OysterVault.jitFetch` is assigned during
   * bundle load and this factory can be constructed before that completes.
   */
  function gatedFetch(url, opts) {
    if (typeof baseFetch === 'function') return baseFetch(url, opts);
    const v = (typeof window !== 'undefined' && window.OysterVault) || null;
    const jf = v && v.jitFetch;
    if (typeof jf === 'function') return jf(url, opts);
    throw new Error('ERR_BLOB_LOCK_NO_GATED_TRANSPORT: no baseFetch was supplied and '
      + 'window.OysterVault.jitFetch is unavailable. /auth/vault/blob is a JIT-gated content '
      + 'prefix — falling back to the bare global fetch would strip the grant, so this refuses.');
  }

  /** The salt the expected proof was recorded against, from the CANONICAL envelope. */
  async function fetchSalt(canonicalId) {
    const base = KEY_PREFIX + encodeURIComponent(canonicalId);
    const grantRes = await gatedFetch(base + '/unlock-grant', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!grantRes || !grantRes.ok) return null;
    const grantBody = await readJsonSafely(grantRes);
    if (!grantBody || typeof grantBody.grant !== 'string') return null;

    const envRes = await gatedFetch(withQuery(base, 'grant', grantBody.grant), {
      method: 'GET',
      credentials: 'same-origin',
    });
    if (!envRes || !envRes.ok) return null;
    const envBody = await readJsonSafely(envRes);
    const env = envBody && (envBody.keyWrap || envBody.pinWrap);
    if (!env) return null;
    // UNION salt, matching `pin-client.unwrapKeyEnvelope`: `saltKey` is the
    // T576 name, `saltPin` the legacy one. Same bytes either way, so the proof
    // is the same whatever the envelope calls the field.
    if (typeof env.saltKey === 'string' && env.saltKey.length >= 32) return env.saltKey;
    if (typeof env.saltPin === 'string' && env.saltPin.length >= 32) return env.saltPin;
    return null;
  }

  async function retryMutation(url, opts, proof) {
    let body = {};
    const raw = opts && opts.body;
    if (typeof raw === 'string' && raw.length) {
      try { body = JSON.parse(raw); } catch (_e) { return null; }
    } else if (raw && typeof raw === 'object') {
      body = raw;
    }
    if (!body || typeof body !== 'object' || Array.isArray(body)) return null;

    const next = {};
    for (const k of Object.keys(opts || {})) next[k] = opts[k];
    // A DELETE ships no body today, so it also ships no content-type — and
    // `express.json()` skips a body without one, which would deliver the proof
    // as `undefined`. The caller's own header wins where it set one.
    next.headers = Object.assign({ 'Content-Type': 'application/json' }, (opts && opts.headers) || {});
    next.body = JSON.stringify(Object.assign({}, body, { unlockProof: proof }));
    return gatedFetch(url, next);
  }

  async function retryRead(url, blobId, opts, proof) {
    const mint = await gatedFetch(
      BLOB_PREFIX + encodeURIComponent(blobId) + '/read-grant',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unlockProof: proof }),
      },
    );
    if (!mint || !mint.ok) return null;
    const minted = await readJsonSafely(mint);
    if (!minted || typeof minted.grant !== 'string') return null;
    return gatedFetch(withQuery(url, 'grant', minted.grant), opts);
  }

  return async function blobLockFetch(url, opts) {
    const res = await gatedFetch(url, opts);

    const blobId = blobIdFromUrl(url);
    if (blobId === null) return res;
    if (!res || res.status !== 403) return res;

    const refusal = await readJsonSafely(res);
    if (!refusal || refusal.reason !== RETRYABLE_REASON) return res;

    const canonicalId = canonicalForBlob(blobId);
    let master = resolveMaster(canonicalId);
    if (!master && typeof requireUnlock === 'function') {
      try { await requireUnlock(canonicalId); } catch (_e) { return res; }
      master = resolveMaster(canonicalId);
    }
    // Cannot prove ⇒ relay the server's own refusal. Never a fabricated success,
    // and no grant-store churn on a request that could not have succeeded.
    if (!master) return res;

    const salt = await fetchSalt(canonicalId);
    if (!salt) return res;

    let proof;
    try { proof = computeRemoveProof(master, salt); } catch (_e) { return res; }

    const method = String((opts && opts.method) || 'GET').toUpperCase();
    const retried = method === 'GET' || method === 'HEAD'
      ? await retryRead(url, blobId, opts, proof)
      : await retryMutation(url, opts, proof);
    // ONE retry, ever. A second refusal is the answer, not a new challenge.
    return retried || res;
  };
}

module.exports = {
  createBlobLockFetch,
  RETRYABLE_REASON,
  BLOB_PREFIX,
};
